import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import SearchOverlay from './SearchOverlay';
import './ModelSearchBar.css';

const SEARCH_ICON = 'https://cdn-icons-png.flaticon.com/512/639/639426.png';
const CLOSE_ICON = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="%230b2f3c" opacity="0.8"/></svg>');

// Reusable search bar for model viewer pages. Mirrors the Menu's search pill
// behaviour: a compact search button (top-right) that expands into an input and
// surfaces the shared SearchOverlay.
//
// Props:
//   currentSystemId : the system id the host page belongs to (e.g. 'skeleton').
//   onFocusMesh     : (meshKey) => void  — invoked when the user picks a result
//                     that belongs to the SAME model. The host page drives the
//                     smooth camera rotation via its focus pipeline.
export default function ModelSearchBar({ currentSystemId, onFocusMesh }) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pillRect, setPillRect] = useState(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setTimeout(() => inputRef.current?.focus(), 200);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setPillRect(null);
  }, []);

  const handleIconClick = useCallback(() => {
    if (searchQuery.trim()) {
      setSearchQuery('');
      setTimeout(() => inputRef.current?.focus(), 0);
    } else if (searchOpen) {
      closeSearch();
    } else {
      openSearch();
    }
  }, [searchQuery, searchOpen, closeSearch, openSearch]);

  const handleSearchSelect = useCallback((systemId, meshKey) => {
    if (systemId === currentSystemId && onFocusMesh) {
      onFocusMesh(meshKey);
      closeSearch();
    } else {
      navigate(`/${systemId}`, { state: { focusMesh: meshKey } });
    }
  }, [currentSystemId, onFocusMesh, navigate, closeSearch]);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeSearch(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen, closeSearch]);

  useEffect(() => {
    if (!searchOpen) {
      setPillRect(null);
      return;
    }
    const timer = setTimeout(() => {
      if (wrapperRef.current) {
        const r = wrapperRef.current.getBoundingClientRect();
        setPillRect({ left: r.left, bottom: r.bottom, width: r.width });
      }
    }, 420);
    return () => clearTimeout(timer);
  }, [searchOpen]);

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      if (!searchQuery.trim()) closeSearch();
    }, 180);
  }, [searchQuery, closeSearch]);

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
  }, []);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      closeSearch();
    } else if (e.key === 'Enter') {
      if (searchQuery.trim()) inputRef.current?.blur();
    }
  }, [closeSearch, searchQuery]);

  return (
    <>
      <div
        className={`model-search-pill${searchOpen ? ' is-open' : ''}`}
        ref={wrapperRef}
      >
        <input
          ref={inputRef}
          type="text"
          className="model-search-input"
          placeholder="Search anatomy models..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleInputKeyDown}
        />
        <button
          type="button"
          className="model-search-icon-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleIconClick}
          title={searchQuery.trim() ? 'Clear' : searchOpen ? 'Close' : 'Search'}
          aria-label={searchQuery.trim() ? 'Clear search' : searchOpen ? 'Close search' : 'Search body parts'}
        >
          {searchQuery.trim() ? (
            <img src={CLOSE_ICON} alt="" className="model-search-icon-img" />
          ) : (
            <img src={SEARCH_ICON} alt="" className="model-search-icon-img" />
          )}
        </button>
      </div>

      <AnimatePresence>
        {searchOpen && (
          <SearchOverlay
            query={searchQuery}
            onSelect={handleSearchSelect}
            onClose={closeSearch}
            anchorRect={pillRect}
          />
        )}
      </AnimatePresence>
    </>
  );
}
